import React, { useEffect, useMemo, useState } from 'react';
import { TargetIcon, ClockIcon, CheckCircle2Icon, AlertTriangleIcon, PhoneCallIcon, UsersIcon, TrendingUpIcon, SettingsIcon, BellIcon, SaveIcon, CalendarIcon, BriefcaseIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { Input } from '../ui/Input';
import { Tabs } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getToday, formatCurrency } from '../../utils/helpers';
// ==================== CONFIGURACIÓN ====================
interface GoalsConfig {
  maxMinutesToSchedule: number; // Tiempo máximo para agendar (minutos)
  warningMinutesBefore: number; // Minutos antes del vencimiento para alerta amarilla
  initialArea: string; // Área responsable inicial (solo texto/etiqueta mostrado en UI)
  escalationArea: string; // Área de escalamiento (solo texto/etiqueta mostrado en UI)
  reminders: number; // Número de recordatorios antes del vencimiento
  receptionDailyAppointmentsTarget: number;
  receptionMonthlyAppointmentsTarget: number;
  receptionDailySalesTarget: number;
  receptionMonthlySalesTarget: number;
  callcenterDailyAppointmentsTarget: number;
  callcenterMonthlyAppointmentsTarget: number;
}
const DEFAULT_CONFIG: GoalsConfig = {
  maxMinutesToSchedule: 120,
  warningMinutesBefore: 30,
  initialArea: 'Recepción',
  escalationArea: 'Call Center',
  reminders: 1,
  receptionDailyAppointmentsTarget: 20,
  receptionMonthlyAppointmentsTarget: 500,
  receptionDailySalesTarget: 1000,
  receptionMonthlySalesTarget: 30000,
  callcenterDailyAppointmentsTarget: 30,
  callcenterMonthlyAppointmentsTarget: 800
};
const CONFIG_KEY = 'savidna-goals-config';
function loadConfig(): GoalsConfig {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (raw) return {
      ...DEFAULT_CONFIG,
      ...JSON.parse(raw)
    };
  } catch {

    /* noop */}
  return DEFAULT_CONFIG;
}
function saveConfig(cfg: GoalsConfig) {
  localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
}
// Evita valores negativos / NaN en los inputs numéricos de metas
function clampNonNegative(value: string): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}
// ==================== TIPOS DE META ====================
type GoalStatus = 'cumplida' | 'proxima_a_vencer' | 'vencida' | 'pendiente';
// El "responsable" de una meta es siempre uno de estos dos roles funcionales,
// independientemente de cómo se llame el área en la configuración (texto editable).
type ResponsibleRole = 'inicial' | 'escalado';
interface PatientGoal {
  patientId: string;
  patientName: string;
  createdAt: Date;
  deadline: Date;
  scheduledAt: Date | null; // Fecha en la que se agendó la primera cita (si existe)
  status: GoalStatus;
  responsibleRole: ResponsibleRole;
  responsibleLabel: string; // Etiqueta visible (config.initialArea / config.escalationArea)
  minutesRemaining: number;
}
function safeDate(d: any): Date {
  const parsed = d instanceof Date ? d : new Date(d);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}
import { usePagination } from '../../hooks/usePagination';
import { supabase } from '../../utils/supabase';
import { normalizeRoleName } from '../../types';
export function Goals() {
  const {
    patients,
    appointments,
    getService,
    isLoading
  } = useData();
  const {
    canView,
    canEdit,
    permissions
  } = usePermissions();
  const {
    isCallCenter,
    isReceptionist
  } = useRolePermissions();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const [config, setConfig] = useState<GoalsConfig>(DEFAULT_CONFIG);
  const [draftConfig, setDraftConfig] = useState<GoalsConfig>(DEFAULT_CONFIG);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [now, setNow] = useState(new Date());
  const [usersMap, setUsersMap] = useState<Record<string, string>>({});
  const canManage = canEdit('goals') || normalizeRoleName(user?.role || '') === 'administrador';
  const today = getToday();
  const currentMonthPrefix = today.substring(0, 7);
  // Cargar mapa de usuarios para saber el rol de quien creó cada cita
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const {
          data
        } = await supabase.from('users').select('id, role');
        if (data) {
          const map: Record<string, string> = {};
          data.forEach(u => {
            map[u.id] = normalizeRoleName(u.role || '');
          });
          setUsersMap(map);
        }
      } catch (err) {
        console.error('Error loading users for goals:', err);
      }
    };
    fetchUsers();
  }, []);
  // Filtros
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  // Cargar configuración desde Supabase (goal_configs)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('goal_configs').select('*').limit(1).maybeSingle();
        if (error) throw error;
        if (data) {
          const cfg: GoalsConfig = {
            maxMinutesToSchedule: data.max_minutes_to_schedule ?? DEFAULT_CONFIG.maxMinutesToSchedule,
            warningMinutesBefore: data.warning_minutes_before ?? DEFAULT_CONFIG.warningMinutesBefore,
            initialArea: data.initial_area ?? DEFAULT_CONFIG.initialArea,
            escalationArea: data.escalation_area ?? DEFAULT_CONFIG.escalationArea,
            reminders: data.reminders ?? DEFAULT_CONFIG.reminders,
            receptionDailyAppointmentsTarget: data.reception_daily_appointments_target ?? DEFAULT_CONFIG.receptionDailyAppointmentsTarget,
            receptionMonthlyAppointmentsTarget: data.reception_monthly_appointments_target ?? DEFAULT_CONFIG.receptionMonthlyAppointmentsTarget,
            receptionDailySalesTarget: data.reception_daily_sales_target ?? DEFAULT_CONFIG.receptionDailySalesTarget,
            receptionMonthlySalesTarget: data.reception_monthly_sales_target ?? DEFAULT_CONFIG.receptionMonthlySalesTarget,
            callcenterDailyAppointmentsTarget: data.callcenter_daily_appointments_target ?? DEFAULT_CONFIG.callcenterDailyAppointmentsTarget,
            callcenterMonthlyAppointmentsTarget: data.callcenter_monthly_appointments_target ?? DEFAULT_CONFIG.callcenterMonthlyAppointmentsTarget
          };
          setConfig(cfg);
          setDraftConfig(cfg);
          localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
        } else {
          const local = loadConfig();
          setConfig(local);
          setDraftConfig(local);
        }
      } catch (err) {
        console.error('Error loading goals config:', err);
        const local = loadConfig();
        setConfig(local);
        setDraftConfig(local);
        showToast({
          title: 'No se pudo cargar la configuración desde el servidor, usando valores locales',
          type: 'warning'
        });
      } finally {
        setIsConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);
  // Índice de precios de servicios: evita llamar getService repetidamente
  // dentro del forEach de citas (que puede ser O(n) por llamada).
  const performanceStats = useMemo(() => {
    let receptionDailyAppts = 0;
    let receptionMonthlyAppts = 0;
    let receptionDailySales = 0;
    let receptionMonthlySales = 0;
    let callcenterDailyAppts = 0;
    let callcenterMonthlyAppts = 0;
    const priceCache = new Map<string, number>();
    const getPrice = (serviceId: string): number => {
      if (priceCache.has(serviceId)) return priceCache.get(serviceId)!;
      const s = getService(serviceId);
      const price = s ? s.price : 0;
      priceCache.set(serviceId, price);
      return price;
    };
    appointments.forEach(apt => {
      const creatorRole = apt.createdByUserId ? usersMap[apt.createdByUserId] : '';
      const isReceptionApt = creatorRole === 'recepcion' || creatorRole === 'recepcionista';
      const aptIsCallCenter = creatorRole === 'callcenter' || creatorRole === 'call_center';
      if (apt.status !== 'cancelada') {
        if (apt.date === today) {
          if (isReceptionApt) receptionDailyAppts++;
          if (aptIsCallCenter) callcenterDailyAppts++;
        }
        if (apt.date.startsWith(currentMonthPrefix)) {
          if (isReceptionApt) receptionMonthlyAppts++;
          if (aptIsCallCenter) callcenterMonthlyAppts++;
        }
      }
      if (apt.status === 'completada') {
        const aptServiceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
        // Se aplica el descuento guardado en la cita (oferta, paquete de
        // sesiones, certificado de regalo o canje de fidelidad al 100%).
        // Antes se sumaba el precio de lista, así que las ventas salían
        // infladas con dinero que nunca entró.
        const aptDiscounts = apt.serviceDiscounts || {};
        let aptRevenue = 0;
        aptServiceIds.forEach(sId => {
          const basePrice = getPrice(sId);
          const esFidelidad = apt.loyaltyServiceId && String(apt.loyaltyServiceId) === String(sId);
          if (esFidelidad) return; // sesión regalada: RD$0
          const discountPercent = Number(aptDiscounts[sId] || 0);
          aptRevenue += Math.max(0, basePrice - basePrice * (discountPercent / 100));
        });
        if (apt.date === today) {
          if (isReceptionApt) receptionDailySales += aptRevenue;
        }
        if (apt.date.startsWith(currentMonthPrefix)) {
          if (isReceptionApt) receptionMonthlySales += aptRevenue;
        }
      }
    });
    return {
      receptionDailyAppts,
      receptionMonthlyAppts,
      receptionDailySales,
      receptionMonthlySales,
      callcenterDailyAppts,
      callcenterMonthlyAppts
    };
  }, [appointments, today, currentMonthPrefix, getService, usersMap]);
  const handleSaveConfig = () => {
    if (!draftConfig.initialArea.trim()) {
      showToast({
        title: 'El área responsable inicial es obligatoria',
        type: 'error'
      });
      return;
    }
    if (!draftConfig.escalationArea.trim()) {
      showToast({
        title: 'El área de escalamiento es obligatoria',
        type: 'error'
      });
      return;
    }
    if (draftConfig.warningMinutesBefore >= draftConfig.maxMinutesToSchedule) {
      showToast({
        title: 'El tiempo de aviso previo debe ser menor al tiempo máximo para agendar',
        type: 'error'
      });
      return;
    }
    const saveToSupabase = async () => {
      try {
        // Primero verificamos si ya existe una fila
        const {
          data: existing
        } = await supabase.from('goal_configs').select('id').limit(1).maybeSingle();
        const payload = {
          max_minutes_to_schedule: draftConfig.maxMinutesToSchedule,
          warning_minutes_before: draftConfig.warningMinutesBefore,
          initial_area: draftConfig.initialArea,
          escalation_area: draftConfig.escalationArea,
          reminders: draftConfig.reminders,
          reception_daily_appointments_target: draftConfig.receptionDailyAppointmentsTarget,
          reception_monthly_appointments_target: draftConfig.receptionMonthlyAppointmentsTarget,
          reception_daily_sales_target: draftConfig.receptionDailySalesTarget,
          reception_monthly_sales_target: draftConfig.receptionMonthlySalesTarget,
          callcenter_daily_appointments_target: draftConfig.callcenterDailyAppointmentsTarget,
          callcenter_monthly_appointments_target: draftConfig.callcenterMonthlyAppointmentsTarget,
          updated_at: new Date().toISOString()
        };
        let error;
        if (existing) {
          const {
            error: updateError
          } = await supabase.from('goal_configs').update(payload).eq('id', existing.id);
          error = updateError;
        } else {
          const {
            error: insertError
          } = await supabase.from('goal_configs').insert(payload);
          error = insertError;
        }
        if (error) throw error;
        saveConfig(draftConfig);
        setConfig(draftConfig);
        setShowConfig(false);
        showToast({
          title: 'Configuración de metas guardada exitosamente',
          type: 'success'
        });
      } catch (err) {
        console.error('Error saving goals config:', err);
        showToast({
          title: 'Error al guardar la configuración',
          type: 'error'
        });
      }
    };
    saveToSupabase();
  };
  // Refresca el reloj cada 30s para que los contadores/alertas se actualicen solos
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  // ==================== CÁLCULO DE METAS ====================
  const goals: PatientGoal[] = useMemo(() => {
    const maxMs = config.maxMinutesToSchedule * 60000;
    const warnMs = config.warningMinutesBefore * 60000;
    return patients.map(p => {
      const createdAt = safeDate(p.createdAt);
      const deadline = new Date(createdAt.getTime() + maxMs);
      // Primera cita creada para este paciente (cualquier estado, ya que "agendar" es el objetivo)
      const firstAppt = appointments.filter(a => a.patientId === p.id).sort((a, b) => safeDate(a.createdAt).getTime() - safeDate(b.createdAt).getTime())[0];
      const scheduledAt = firstAppt ? safeDate(firstAppt.createdAt) : null;
      const minutesRemaining = Math.round((deadline.getTime() - now.getTime()) / 60000);
      let status: GoalStatus = 'pendiente';
      let responsibleRole: ResponsibleRole = 'inicial';
      if (scheduledAt && scheduledAt.getTime() <= deadline.getTime()) {
        status = 'cumplida';
      } else if (!scheduledAt && now.getTime() > deadline.getTime()) {
        status = 'vencida';
        responsibleRole = 'escalado';
      } else if (!scheduledAt && deadline.getTime() - now.getTime() <= warnMs) {
        status = 'proxima_a_vencer';
      } else if (scheduledAt && scheduledAt.getTime() > deadline.getTime()) {
        // Se agendó pero tarde: igualmente se considera vencida/escalada
        status = 'vencida';
        responsibleRole = 'escalado';
      }
      const responsibleLabel = responsibleRole === 'escalado' ? config.escalationArea : config.initialArea;
      return {
        patientId: p.id,
        patientName: p.name,
        createdAt,
        deadline,
        scheduledAt,
        status,
        responsibleRole,
        responsibleLabel,
        minutesRemaining
      };
    }).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [patients, appointments, config, now]);
  // Filtra por el ROL real del usuario logueado, no por el texto editable
  // de config.initialArea/escalationArea (que un admin puede renombrar
  // en cualquier momento sin que eso deba afectar los permisos de vista).
  const roleFilteredGoals = useMemo(() => {
    return goals.filter(g => {
      if (isCallCenter && g.responsibleRole !== 'escalado') return false;
      if (isReceptionist && g.responsibleRole !== 'inicial') return false;
      return true;
    });
  }, [goals, isCallCenter, isReceptionist]);
  // ==================== KPIs ====================
  const kpis = useMemo(() => {
    const totalNuevos = roleFilteredGoals.length;
    const agendadas = roleFilteredGoals.filter(g => g.status === 'cumplida').length;
    const vencidas = roleFilteredGoals.filter(g => g.status === 'vencida').length;
    const proximas = roleFilteredGoals.filter(g => g.status === 'proxima_a_vencer').length;
    const tiemposCumplidos = roleFilteredGoals.filter(g => g.status === 'cumplida' && g.scheduledAt).map(g => (g.scheduledAt!.getTime() - g.createdAt.getTime()) / 60000);
    const tiempoPromedio = tiemposCumplidos.length ? Math.round(tiemposCumplidos.reduce((s, v) => s + v, 0) / tiemposCumplidos.length) : 0;
    const cumplimientoPct = totalNuevos ? Math.round(agendadas / totalNuevos * 100) : 0;
    const recuperacionPct = vencidas ? Math.round(roleFilteredGoals.filter(g => g.status === 'vencida' && g.scheduledAt).length / vencidas * 100) : 0;
    return {
      totalNuevos,
      agendadas,
      vencidas,
      proximas,
      tiempoPromedio,
      cumplimientoPct,
      recuperacionPct
    };
  }, [roleFilteredGoals]);
  const alerts = useMemo(() => roleFilteredGoals.filter(g => g.status === 'proxima_a_vencer' || g.status === 'vencida'), [roleFilteredGoals]);
  const filteredGoals = useMemo(() => {
    return roleFilteredGoals.filter(g => {
      if (filterStatus && g.status !== filterStatus) return false;
      const createdDateStr = g.createdAt.toISOString().split('T')[0];
      if (filterDateFrom && createdDateStr < filterDateFrom) return false;
      if (filterDateTo && createdDateStr > filterDateTo) return false;
      return true;
    });
  }, [roleFilteredGoals, filterStatus, filterDateFrom, filterDateTo]);
  const {
    paginatedItems,
    currentPage,
    totalPages,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredGoals, {
    itemsPerPage: 10
  });
  if (isLoading || isConfigLoading || !permissions) {
    return <div className="flex items-center justify-center py-20" data-id="element-3543" data-mp="3n8dm139fs3js">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" data-id="element-3544" data-mp="3s0nbyjajx3m2"></div>
      </div>;
  }
  if (!canView('goals')) {
    return <Card className="p-8 text-center text-gray-500 dark:text-gray-400" data-id="element-3545" data-mp="3w4cuos4joqz6">
        No tienes permiso para ver este módulo. Pide a un administrador que te
        habilite acceso en Configuración → Roles y Permisos → &quot;Metas y
        Alertas&quot;.
      </Card>;
  }
  const statusBadge = (status: GoalStatus) => {
    switch (status) {
      case 'cumplida':
        return <Badge variant="success" dot data-id="element-3546" data-mp="2bnzw1cpde3on">
            Cumplida
          </Badge>;
      case 'proxima_a_vencer':
        return <Badge variant="warning" dot data-id="element-3547" data-mp="xq3x9lnl0dz2">
            Próxima a vencer
          </Badge>;
      case 'vencida':
        return <Badge variant="danger" dot data-id="element-3548" data-mp="sz5f0gj195t6">
            Vencida / Escalada
          </Badge>;
      default:
        return <Badge variant="info" dot data-id="element-3549" data-mp="oybn7c2gz6v0">
            Pendiente
          </Badge>;
    }
  };
  return <div className="space-y-8 pb-10" data-id="element-3550" data-mp="k0a62wolthm6">
      {/* Encabezado Moderno */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-800 dark:to-gray-900 p-8 sm:p-10 shadow-xl" data-id="element-3551" data-mp="1gp9f4dx0inff">
        {/* Elementos decorativos */}
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-gradient-to-br from-[#1BACA6] to-[#1890C8] rounded-full opacity-20 blur-3xl" data-id="element-3552" data-mp="17nia7baaqkmm"></div>
        <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-24 h-24 bg-gradient-to-br from-[#1BACA6] to-[#1890C8] rounded-full opacity-20 blur-2xl" data-id="element-3553" data-mp="12rkth3v4fc9b"></div>

        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-id="element-3554" data-mp="3neo0rh8xoijs">
          <div className="flex items-center gap-5" data-id="element-3555" data-mp="2yq3pgui738k">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1BACA6] to-[#1890C8] flex items-center justify-center shadow-lg shadow-[#1BACA6]/30" data-id="element-3556" data-mp="3ubayfqpis3mt">
              <TargetIcon className="w-7 h-7 text-white" data-id="element-3557" data-mp="3panajdwl82wt" />
            </div>
            <div data-id="element-3558" data-mp="3kf7wakuo44zv">
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-1" data-id="element-3559" data-mp="3kbk2rlp83il5">
                Metas y Alertas
              </h1>
              <p className="text-gray-400 text-sm sm:text-base" data-id="element-3560" data-mp="gwej495sxi68">
                Agendar Paciente Nuevo · Límite de{' '}
                <span className="text-white font-medium" data-id="element-3561" data-mp="bwjvqfl8japx">
                  {config.maxMinutesToSchedule} min
                </span>
              </p>
            </div>
          </div>
          {canManage && <Button variant="secondary" aria-expanded={showConfig} aria-controls="goals-config-panel" onClick={() => setShowConfig(s => !s)} className="bg-white/10 hover:bg-white/20 text-white border-none shadow-none backdrop-blur-sm transition-all" data-id="element-3562" data-mp="2woviwugnlg1w">
            
              <SettingsIcon className="w-4 h-4 mr-2" data-id="element-3563" data-mp="1c3ysz4g3dej1" />
              Configuración
            </Button>}
        </div>
      </div>

      {/* Panel de configuración (solo admin / roles con permiso "full") */}
      {showConfig && canManage && <Card id="goals-config-panel" className="border-gray-200 dark:border-gray-700 shadow-lg animate-fade-in" data-id="element-3564" data-mp="1qvpfmcehdi55">
        
          <div className="flex items-center justify-between mb-5" data-id="element-3565" data-mp="1uupyfuwykks5">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 text-lg" data-id="element-3566" data-mp="1zwnnj5s1i9t0">
              <SettingsIcon className="w-5 h-5 text-[#1BACA6]" data-id="element-3567" data-mp="24te2tqw0t1dd" /> Configuración de
              la meta
            </h3>
          </div>

          <Tabs variant="pills" tabs={[{
        id: 'general',
        label: 'General',
        mobileLabel: 'General',
        icon: <ClockIcon className="w-4 h-4" data-id="element-3569" data-mp="2gi6ogazhoo4" />,
        content: <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3570" data-mp="3ukkfd56uz64o">
                  <div data-id="element-3571" data-mp="ce64jf3vzcai">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block" data-id="element-3572" data-mp="cefvb4wr9nh9">
                      Tiempo máximo para agendar
                    </label>
                    <Select value={String(draftConfig.maxMinutesToSchedule)} onChange={v => setDraftConfig(c => ({
              ...c,
              maxMinutesToSchedule: Number(v)
            }))} searchable={false} clearable={false} options={[{
              value: '30',
              label: '30 minutos'
            }, {
              value: '60',
              label: '1 hora'
            }, {
              value: '120',
              label: '2 horas'
            }, {
              value: '240',
              label: '4 horas'
            }, {
              value: '1440',
              label: '24 horas'
            }]} data-id="element-3573" data-mp="lem6gk4zebd9" />
                
                  </div>
                  <div data-id="element-3574" data-mp="101b045cjxubq">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block" data-id="element-3575" data-mp="zzzz2dahr0ob">
                      Aviso previo al vencimiento
                    </label>
                    <Select value={String(draftConfig.warningMinutesBefore)} onChange={v => setDraftConfig(c => ({
              ...c,
              warningMinutesBefore: Number(v)
            }))} searchable={false} clearable={false} options={[{
              value: '10',
              label: '10 minutos antes'
            }, {
              value: '15',
              label: '15 minutos antes'
            }, {
              value: '30',
              label: '30 minutos antes'
            }, {
              value: '60',
              label: '1 hora antes'
            }]} data-id="element-3576" data-mp="r1xpvaglt1py" />
                
                  </div>
                  <div data-id="element-3577" data-mp="11p7mscbqp97i">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block" data-id="element-3578" data-mp="wo0gyit5qk3h">
                      Área responsable inicial
                    </label>
                    <Input value={draftConfig.initialArea} onChange={e => setDraftConfig(c => ({
              ...c,
              initialArea: e.target.value
            }))} data-id="element-3579" data-mp="rr1ixp6dht00" />
                    <p className="text-xs text-gray-400 mt-1" data-id="element-3580" data-mp="2tlxzm4eyjkmc">
                      Solo es una etiqueta visible; no cambia quién puede ver
                      cada meta (eso depende del rol del usuario).
                    </p>
                  </div>
                  <div data-id="element-3581" data-mp="38b3u5f2uomnw">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block" data-id="element-3582" data-mp="38ac22e8gp72d">
                      Área de escalamiento
                    </label>
                    <Input value={draftConfig.escalationArea} onChange={e => setDraftConfig(c => ({
              ...c,
              escalationArea: e.target.value
            }))} data-id="element-3583" data-mp="2fm6e7im431yb" />
                    <p className="text-xs text-gray-400 mt-1" data-id="element-3584" data-mp="3w0y9q4ek0pbu">
                      Solo es una etiqueta visible; no cambia quién puede ver
                      cada meta (eso depende del rol del usuario).
                    </p>
                  </div>
                  <div data-id="element-3585" data-mp="20wi5x9sx9gt6">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block" data-id="element-3586" data-mp="20zw4s82dn7vv">
                      Número de recordatorios
                    </label>
                    <Select value={String(draftConfig.reminders)} onChange={v => setDraftConfig(c => ({
              ...c,
              reminders: Number(v)
            }))} searchable={false} clearable={false} options={[{
              value: '1',
              label: '1 recordatorio'
            }, {
              value: '2',
              label: '2 recordatorios'
            }, {
              value: '3',
              label: '3 recordatorios'
            }]} data-id="element-3587" data-mp="29xy32fuq7mfg" />
                
                  </div>
                </div>
      }, {
        id: 'reception',
        label: 'Metas Recepción',
        mobileLabel: 'Recepción',
        icon: <UsersIcon className="w-4 h-4" data-id="element-3588" data-mp="afmiqpjcpixc" />,
        content: <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3589" data-mp="ahftoh0zypb5">
                  <div data-id="element-3590" data-mp="ff7j94e65zc6">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block" data-id="element-3591" data-mp="fcd15jimoqlx">
                      Citas Diarias
                    </label>
                    <Input type="number" min="0" value={draftConfig.receptionDailyAppointmentsTarget} onChange={e => setDraftConfig(c => ({
              ...c,
              receptionDailyAppointmentsTarget: clampNonNegative(e.target.value)
            }))} data-id="element-3592" data-mp="182v2aezskhwp" />
                
                  </div>
                  <div data-id="element-3593" data-mp="304lzwbhvm42s">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block" data-id="element-3594" data-mp="305x1q5rrh1r9">
                      Citas Mensuales
                    </label>
                    <Input type="number" min="0" value={draftConfig.receptionMonthlyAppointmentsTarget} onChange={e => setDraftConfig(c => ({
              ...c,
              receptionMonthlyAppointmentsTarget: clampNonNegative(e.target.value)
            }))} data-id="element-3595" data-mp="2q6fx7ro8wtwv" />
                
                  </div>
                  <div data-id="element-3596" data-mp="3940k4t7g9ieu">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block" data-id="element-3597" data-mp="3937exwo530yt">
                      Ventas Diarias ($)
                    </label>
                    <Input type="number" min="0" value={draftConfig.receptionDailySalesTarget} onChange={e => setDraftConfig(c => ({
              ...c,
              receptionDailySalesTarget: clampNonNegative(e.target.value)
            }))} data-id="element-3598" data-mp="5hrw5qvy08pl" />
                
                  </div>
                  <div data-id="element-3599" data-mp="1sfaudy6wgu5l">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block" data-id="element-3600" data-mp="1xc1dyvhwy7vr">
                      Ventas Mensuales ($)
                    </label>
                    <Input type="number" min="0" value={draftConfig.receptionMonthlySalesTarget} onChange={e => setDraftConfig(c => ({
              ...c,
              receptionMonthlySalesTarget: clampNonNegative(e.target.value)
            }))} data-id="element-3601" data-mp="229jmiks0ql38" />
                
                  </div>
                </div>
      }, {
        id: 'callcenter',
        label: 'Metas Call Center',
        mobileLabel: 'Call Center',
        icon: <PhoneCallIcon className="w-4 h-4" data-id="element-3602" data-mp="3qefo1dg63fyj" />,
        content: <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-3603" data-mp="3vcqynqeaduht">
                  <div data-id="element-3604" data-mp="3hcg7em92edw4">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block" data-id="element-3605" data-mp="3hczet9139jvw">
                      Citas Diarias
                    </label>
                    <Input type="number" min="0" value={draftConfig.callcenterDailyAppointmentsTarget} onChange={e => setDraftConfig(c => ({
              ...c,
              callcenterDailyAppointmentsTarget: clampNonNegative(e.target.value)
            }))} data-id="element-3606" data-mp="dufviw0e1od8" />
                
                  </div>
                  <div data-id="element-3607" data-mp="wrpfce9ml3v0">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block" data-id="element-3608" data-mp="11k0hzovr33r9">
                      Citas Mensuales
                    </label>
                    <Input type="number" min="0" value={draftConfig.callcenterMonthlyAppointmentsTarget} onChange={e => setDraftConfig(c => ({
              ...c,
              callcenterMonthlyAppointmentsTarget: clampNonNegative(e.target.value)
            }))} data-id="element-3609" data-mp="1awpqs6s15giu" />
                
                  </div>
                </div>
      }]} data-id="element-3568" data-mp="5jxsgkpxquk1" />
          

          <div className="mt-6 flex justify-end pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-3610" data-mp="11ye58j60t8x9">
            <Button onClick={handleSaveConfig} className="bg-gradient-to-r from-[#1BACA6] to-[#1890C8] hover:from-[#159690] hover:to-[#1276a6] text-white shadow-md" data-id="element-3611" data-mp="wzay8t6hcsmn">
            
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-3612" data-mp="28eq4knkkwwuf" />
              Guardar configuración
            </Button>
          </div>
        </Card>}

      {/* KPIs - Actividad del día */}
      <div className="space-y-4" data-id="element-3613" data-mp="2ghi9p2r70ro6">
        <h3 className="font-bold text-gray-900 dark:text-white text-lg flex items-center gap-2" data-id="element-3614" data-mp="2kfzkpxdqimav">
          <UsersIcon className="w-5 h-5 text-gray-400" data-id="element-3615" data-mp="2pfkjo7swmunl" />
          Actividad de pacientes nuevos
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-3616" data-mp="2udvtiij7955t">
          <KpiCard icon={<UsersIcon className="w-6 h-6" data-id="element-3618" data-mp="1ws7zg4a7uyxg" />} label="Pacientes nuevos" value={kpis.totalNuevos} data-id="element-3617" data-mp="1wroq3bymtiv1" />
          

          <KpiCard icon={<CheckCircle2Icon className="w-6 h-6" data-id="element-3620" data-mp="26pcihv8rmea1" />} label="Citas agendadas a tiempo" value={kpis.agendadas} accent="success" data-id="element-3619" data-mp="21o6rq2yo1135" />
          

          <KpiCard icon={<BellIcon className="w-6 h-6" data-id="element-3622" data-mp="1e0yl9hpi3lzj" />} label="Próximas a vencer" value={kpis.proximas} accent="warning" data-id="element-3621" data-mp="2bi5r4u7cu6re" />
          

          <KpiCard icon={<AlertTriangleIcon className="w-6 h-6" data-id="element-3624" data-mp="1ixfcwlvwsihd" />} label="Metas vencidas" value={kpis.vencidas} accent="danger" data-id="element-3623" data-mp="1ixxfe095zsxq" />
          
        </div>
      </div>

      {/* KPIs - Indicadores de gestión */}
      <div className="space-y-4" data-id="element-3625" data-mp="11ha1n5s0evwq">
        <h3 className="font-bold text-gray-900 dark:text-white text-lg flex items-center gap-2" data-id="element-3626" data-mp="11gi77ya5f481">
          <TrendingUpIcon className="w-5 h-5 text-gray-400" data-id="element-3627" data-mp="wigjv15owiqr" />
          Indicadores de gestión
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-3628" data-mp="sippkngyfkhh">
          <KpiCard icon={<ClockIcon className="w-6 h-6" data-id="element-3630" data-mp="nolf3u8hcubn" />} label="Tiempo promedio en agendar" value={`${kpis.tiempoPromedio} min`} data-id="element-3629" data-mp="npwl80peee5x" />
          

          <KpiCard icon={<TrendingUpIcon className="w-6 h-6" data-id="element-3632" data-mp="du1wqoyhtov8" />} label="% Cumplimiento Recepción" value={`${kpis.cumplimientoPct}%`} accent="success" data-id="element-3631" data-mp="dta1xgcpzv60" />
          

          <KpiCard icon={<PhoneCallIcon className="w-6 h-6" data-id="element-3634" data-mp="4sltd163in0i" />} label="% Recuperación Call Center" value={`${kpis.recuperacionPct}%`} accent="info" data-id="element-3633" data-mp="4t3qe00l159h" />
          
        </div>
      </div>

      {/* Metas de Rendimiento (Performance Goals) */}
      {(normalizeRoleName(user?.role || '') === 'administrador' || normalizeRoleName(user?.role || '') === 'recepcionista') && <div className="space-y-4" data-id="element-3635" data-mp="opx4koxapx4n">
          <h3 className="font-bold text-gray-900 dark:text-white text-lg" data-id="element-3636" data-mp="as8hrbjo4fwy">
            Metas Recepción
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3637" data-mp="frbn6x3k8o5g">
            <PerformanceGoalCard title="Citas Diarias" current={performanceStats.receptionDailyAppts} target={config.receptionDailyAppointmentsTarget} format="number" icon={<CalendarIcon className="w-5 h-5 text-blue-500" data-id="element-3639" data-mp="1w51uecpz7sjb" />} color="blue" data-id="element-3638" data-mp="1rk3qgu9yfk2" />
          
            <PerformanceGoalCard title="Citas Mensuales" current={performanceStats.receptionMonthlyAppts} target={config.receptionMonthlyAppointmentsTarget} format="number" icon={<CalendarIcon className="w-5 h-5 text-blue-500" data-id="element-3641" data-mp="3gox7pttvizic" />} color="blue" data-id="element-3640" data-mp="2076i6utqtuy1" />
          
            <PerformanceGoalCard title="Ventas Diarias" current={performanceStats.receptionDailySales} target={config.receptionDailySalesTarget} format="currency" icon={<TrendingUpIcon className="w-5 h-5 text-emerald-500" data-id="element-3643" data-mp="89n8cthdc8e9" />} color="emerald" data-id="element-3642" data-mp="3ugbt27b97csk" />
          
            <PerformanceGoalCard title="Ventas Mensuales" current={performanceStats.receptionMonthlySales} target={config.receptionMonthlySalesTarget} format="currency" icon={<BriefcaseIcon className="w-5 h-5 text-purple-500" data-id="element-3645" data-mp="2iz281gch8cp5" />} color="purple" data-id="element-3644" data-mp="3asdgt64mtds" />
          
          </div>
        </div>}

      {(normalizeRoleName(user?.role || '') === 'administrador' || normalizeRoleName(user?.role || '') === 'callcenter') && <div className="space-y-4" data-id="element-3646" data-mp="hzp6x2txuvfc">
          <h3 className="font-bold text-gray-900 dark:text-white text-lg" data-id="element-3647" data-mp="mvnqmd4bnp9f">
            Metas Call Center
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-3648" data-mp="ruqxm33v45k1">
            <PerformanceGoalCard title="Citas Diarias" current={performanceStats.callcenterDailyAppts} target={config.callcenterDailyAppointmentsTarget} format="number" icon={<CalendarIcon className="w-5 h-5 text-blue-500" data-id="element-3650" data-mp="15u9shu7nxkee" />} color="blue" data-id="element-3649" data-mp="vxn8f7n5uxdf" />
          
            <PerformanceGoalCard title="Citas Mensuales" current={performanceStats.callcenterMonthlyAppts} target={config.callcenterMonthlyAppointmentsTarget} format="number" icon={<CalendarIcon className="w-5 h-5 text-blue-500" data-id="element-3652" data-mp="1joj6gmi7mot4" />} color="blue" data-id="element-3651" data-mp="1an2xmj71mur0" />
          
          </div>
        </div>}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8" data-id="element-3653" data-mp="393lkr3nzsmdd">
        {/* Alertas activas */}
        <div className="xl:col-span-1" data-id="element-3654" data-mp="344qu5fjs9tip">
          <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden h-full flex flex-col" data-id="element-3655" data-mp="343z5ypsimy2g">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-3656" data-mp="2v4c3e0o1vm7t">
              <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2.5 text-lg" data-id="element-3657" data-mp="3ruut39m1153d">
                <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center" data-id="element-3658" data-mp="3nshnqmb6z974">
                  <BellIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" data-id="element-3659" data-mp="3j0q7b4lr2jup" />
                </div>
                Alertas activas ({alerts.length})
              </h3>
            </div>
            <div className="p-6 flex-1 overflow-y-auto" style={{
            maxHeight: '600px'
          }} data-id="element-3660" data-mp="fdzl5040sluw">
              
              {alerts.length === 0 ? <div className="text-center py-12 flex flex-col items-center justify-center h-full" data-id="element-3661" data-mp="3f2zly5n433l7">
                  <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800/50 rounded-full flex items-center justify-center mb-4" data-id="element-3662" data-mp="3jvj1371g66s4">
                    <CheckCircle2Icon className="w-8 h-8 text-emerald-500" data-id="element-3663" data-mp="353ryuqqkrukw" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-3664" data-mp="3a2v52eiak6ug">
                    Todo al día
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 max-w-[200px]" data-id="element-3665" data-mp="6oqyfbzg52zw">
                    Excelente, no hay alertas activas en este momento.
                  </p>
                </div> : <div className="space-y-4" data-id="element-3666" data-mp="3sunxa6pjdo14">
                  {alerts.map(g => <div key={g.patientId} className={`relative overflow-hidden p-5 rounded-2xl bg-white dark:bg-gray-800 border shadow-sm hover:shadow-md transition-all flex flex-col gap-3 group ${g.status === 'vencida' ? 'border-red-100 dark:border-red-500/20 hover:border-red-200 dark:hover:border-red-500/30' : 'border-amber-100 dark:border-amber-500/20 hover:border-amber-200 dark:hover:border-amber-500/30'}`} data-id="element-3667" data-mp="2dex5wyf5xhhc">
                  
                      {/* Left colored border accent */}
                      <div className={`absolute left-0 top-0 bottom-0 w-1.5 transition-colors ${g.status === 'vencida' ? 'bg-red-500 group-hover:bg-red-600' : 'bg-amber-500 group-hover:bg-amber-600'}`} data-id="element-3668" data-mp="2ewvwrssqgtp0" />
                  

                      <div className="flex items-start gap-3 pl-2" data-id="element-3669" data-mp="1m5c6joxpyfdi">
                        <div className={`mt-0.5 w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${g.status === 'vencida' ? 'bg-red-50 dark:bg-red-500/10 text-red-500' : 'bg-amber-50 dark:bg-amber-500/10 text-amber-500'}`} data-id="element-3670" data-mp="20xmfepssjtjg">
                      
                          {g.status === 'vencida' ? <AlertTriangleIcon className="w-5 h-5" data-id="element-3671" data-mp="13eupwiq5va8i" /> : <ClockIcon className="w-5 h-5" data-id="element-3672" data-mp="3qmdq199fmdb" />}
                        </div>
                        <div data-id="element-3673" data-mp="3vvq5nhwb0wmo">
                          <h4 className="text-base font-bold text-gray-900 dark:text-white mb-0.5" data-id="element-3674" data-mp="dq4vcb1p6w27">
                            {g.patientName}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed" data-id="element-3675" data-mp="8p7cdw8v5xzn">
                            {g.status === 'vencida' ? <>
                                <span className="font-semibold text-red-600 dark:text-red-400" data-id="element-3676" data-mp="3c9txrrnzrd3e">
                                  {config.initialArea}
                                </span>{' '}
                                {g.scheduledAt ? 'agendó fuera del tiempo límite. Reasignada a' : 'no agendó a tiempo. Reasignada a'}
                                {' '}
                                <span className="font-semibold text-red-600 dark:text-red-400" data-id="element-3677" data-mp="2tfonlmaznljo">
                                  {config.escalationArea}
                                </span>
                                .
                              </> : <>
                                Próxima a vencer (
                                <span className="font-semibold text-amber-600 dark:text-amber-400" data-id="element-3678" data-mp="13zm5ifqfl94a">
                                  {Math.max(g.minutesRemaining, 0)} min
                                </span>{' '}
                                restantes).
                              </>}
                          </p>
                        </div>
                      </div>
                      <div className="pl-[52px] mt-1" data-id="element-3679" data-mp="1nnlbzedypu3w">
                        {statusBadge(g.status)}
                      </div>
                    </div>)}
                </div>}
            </div>
          </div>
        </div>

        {/* Tabla de metas por paciente */}
        <div className="xl:col-span-2" data-id="element-3680" data-mp="2dyn6ygz7kbqn">
          <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden h-full flex flex-col" data-id="element-3681" data-mp="2dxlxggxubxb1">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3682" data-mp="247slvfxp7i6s">
              <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2.5 text-lg" data-id="element-3683" data-mp="1v7m61nej264g">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center" data-id="element-3684" data-mp="1q89mbbiydf63">
                  <UsersIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" data-id="element-3685" data-mp="1lar9vb5q8htc" />
                </div>
                Metas por paciente nuevo
              </h3>
              <Badge variant="info" className="bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/20" data-id="element-3686" data-mp="3fnh63zjki33w">
                
                {filteredGoals.length} registros
              </Badge>
            </div>

            {/* Filtros */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4 bg-white dark:bg-gray-800 items-end" data-id="element-3687" data-mp="12co4h9emutos">
              <div data-id="element-3688" data-mp="1b5rq44zfmcui">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block" data-id="element-3689" data-mp="1b6axirrghiua">
                  Estado
                </label>
                <Select value={filterStatus} onChange={setFilterStatus} options={[{
                value: '',
                label: 'Todos los estados'
              }, {
                value: 'cumplida',
                label: 'Cumplida'
              }, {
                value: 'pendiente',
                label: 'Pendiente'
              }, {
                value: 'proxima_a_vencer',
                label: 'Próxima a vencer'
              }, {
                value: 'vencida',
                label: 'Vencida / Escalada'
              }]} searchable={false} data-id="element-3690" data-mp="ehd1z7bvtgw2" />
                
              </div>
              <div data-id="element-3691" data-mp="27bur45gwsx6g">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block" data-id="element-3692" data-mp="27bb78j7p7yps">
                  Desde
                </label>
                <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} data-id="element-3693" data-mp="2l9suygxltb7p" />
                
              </div>
              <div data-id="element-3694" data-mp="2v3rv5ae4akx0">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block" data-id="element-3695" data-mp="2q2c6778tju9c">
                  Hasta
                </label>
                <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} data-id="element-3696" data-mp="3436k25emynfe" />
                
              </div>
              {(filterStatus || filterDateFrom || filterDateTo) && <div data-id="element-3697" data-mp="3noovvwcfulkd">
                  <Button variant="secondary" size="sm" onClick={() => {
                setFilterStatus('');
                setFilterDateFrom('');
                setFilterDateTo('');
              }} className="w-full sm:w-auto" data-id="element-3698" data-mp="3nn3w9sb9ocfn">
                  
                    Limpiar filtros
                  </Button>
                </div>}
            </div>

            {/* MÓVIL: tarjetas. La tabla de 5 columnas obligaba a desplazarse
                en horizontal en el celular, así que se sustituye por una lista. */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3699" data-mp="2uxonzaazkxnf">
              {paginatedItems.map(g => <div key={g.patientId} className="p-4" data-id="element-3700" data-mp="2lz35tg9ffkn6">
                  <div className="flex items-start justify-between gap-3" data-id="element-3701" data-mp="2lyjs6bqt7iey">
                    <p className="font-semibold text-gray-900 dark:text-white leading-snug" data-id="element-3702" data-mp="rjwe5hy198gx">
                      {g.patientName}
                    </p>
                    <div className="shrink-0" data-id="element-3703" data-mp="ht1g7gbfckfn">{statusBadge(g.status)}</div>
                  </div>
                  <dl className="mt-2.5 space-y-1 text-xs" data-id="element-3704" data-mp="hqp4hkw9f6b1">
                    <div className="flex justify-between gap-3" data-id="element-3705" data-mp="dt13c03z4hap">
                      <dt className="text-gray-400" data-id="element-3706" data-mp="1ai8mx2l18gbz">Registrado</dt>
                      <dd className="text-gray-600 dark:text-gray-300 text-right" data-id="element-3707" data-mp="1afcqxjsqw1q6">
                        {g.createdAt.toLocaleString('es-ES', {
                      dateStyle: 'short',
                      timeStyle: 'short'
                    })}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3" data-id="element-3708" data-mp="3m79o4m0983sb">
                      <dt className="text-gray-400" data-id="element-3709" data-mp="3m8ugc4z3jan0">Vence</dt>
                      <dd className="text-gray-600 dark:text-gray-300 text-right" data-id="element-3710" data-mp="3h7x2g4l4d2rb">
                        {g.deadline.toLocaleString('es-ES', {
                      dateStyle: 'short',
                      timeStyle: 'short'
                    })}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3" data-id="element-3711" data-mp="25qkbzy4m3xsw">
                      <dt className="text-gray-400" data-id="element-3712" data-mp="2ahiyjfg71s4l">Responsable</dt>
                      <dd className="text-gray-600 dark:text-gray-300 text-right" data-id="element-3713" data-mp="2aitzz8m62nsj">
                        {g.responsibleLabel}
                      </dd>
                    </div>
                  </dl>
                </div>)}
              {paginatedItems.length === 0 && <div className="px-6 py-14 text-center" data-id="element-3714" data-mp="2pg4ih1x88txr">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-50 dark:bg-gray-800" data-id="element-3715" data-mp="2tg4ygqzz5d6x">
                    <UsersIcon className="h-8 w-8 text-gray-400" data-id="element-3716" data-mp="v23n4r0vyp2f" />
                  </div>
                  <p className="font-medium text-gray-500 dark:text-gray-400" data-id="element-3717" data-mp="z3f4yadiq602">
                    No se encontraron registros con los filtros actuales.
                  </p>
                </div>}
            </div>

            {/* ESCRITORIO: tabla */}
            <div className="hidden md:block overflow-x-auto flex-1" data-id="element-3718" data-mp="oy75l4jpuhmc">
              <table className="w-full text-sm" data-id="element-3719" data-mp="p0jkf8u6gbv6">
                <thead data-id="element-3720" data-mp="kwlk4ut48v0j">
                  <tr className="text-left text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 bg-gray-50/80 dark:bg-gray-800/80 border-b border-gray-100 dark:border-gray-700" data-id="element-3721" data-mp="ky552yppvu8n">
                    <th className="px-6 py-4" data-id="element-3722" data-mp="3of547a1n4uk9">Paciente</th>
                    <th className="px-6 py-4" data-id="element-3723" data-mp="68gzhlai69f4">Registrado</th>
                    <th className="px-6 py-4" data-id="element-3724" data-mp="65urxlnm3tww">Vence</th>
                    <th className="px-6 py-4" data-id="element-3725" data-mp="17tat69qy4o2">Responsable</th>
                    <th className="px-6 py-4" data-id="element-3726" data-mp="1hn4bs9anknyq">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3727" data-mp="1co1ghgkh5w3y">
                  {paginatedItems.map(g => <tr key={g.patientId} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors group" data-id="element-3728" data-mp="1l4sb0oy0emha">
                    
                      <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white" data-id="element-3729" data-mp="1b7dw4trqi5r3">
                        {g.patientName}
                      </td>
                      <td className="px-6 py-4 text-gray-500 dark:text-gray-400 font-medium" data-id="element-3730" data-mp="1g81n5ffyqmlb">
                        {g.createdAt.toLocaleString('es-ES', {
                      dateStyle: 'short',
                      timeStyle: 'short'
                    })}
                      </td>
                      <td className="px-6 py-4 text-gray-500 dark:text-gray-400 font-medium" data-id="element-3731" data-mp="1u3m62avoz4sq">
                        {g.deadline.toLocaleString('es-ES', {
                      dateStyle: 'short',
                      timeStyle: 'short'
                    })}
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-gray-300 font-medium" data-id="element-3732" data-mp="2rmf92er5oryp">
                        {g.responsibleLabel}
                      </td>
                      <td className="px-6 py-4" data-id="element-3733" data-mp="2ds9w061j6th">{statusBadge(g.status)}</td>
                    </tr>)}
                  {paginatedItems.length === 0 && <tr data-id="element-3734" data-mp="c4deq5cb57ku">
                      <td colSpan={5} className="px-6 py-16 text-center" data-id="element-3735" data-mp="c3lhkq32b1sg">
                        <div className="flex flex-col items-center justify-center" data-id="element-3736" data-mp="3amir53n57z72">
                          <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-3737" data-mp="3flc4mw2fjq78">
                            <UsersIcon className="w-8 h-8 text-gray-400" data-id="element-3738" data-mp="3kjdlrbgapfg2" />
                          </div>
                          <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-3739" data-mp="3on359mi456u8">
                            No se encontraron registros con los filtros
                            actuales.
                          </p>
                        </div>
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPages > 1 && <div className="p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-3740" data-mp="2kemdgu4abf5h">
                <div className="flex items-center justify-between" data-id="element-3741" data-mp="2amowt7uibuzd">
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3742" data-mp="2akchl2g4vyq0">
                    Página {currentPage} de {totalPages}
                  </span>
                  <div className="flex gap-2" data-id="element-3743" data-mp="20n7t2vytfr61">
                    <Button variant="secondary" size="sm" onClick={prevPage} disabled={currentPage === 1} data-id="element-3744" data-mp="20qlw86fb04ej">
                    
                      Anterior
                    </Button>
                    <Button variant="secondary" size="sm" onClick={nextPage} disabled={currentPage === totalPages} data-id="element-3745" data-mp="3r06qqugw1s1i">
                    
                      Siguiente
                    </Button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}
function KpiCard({
  icon,
  label,
  value,
  accent = 'default'
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  accent?: 'default' | 'success' | 'danger' | 'warning' | 'info';
}) {
  const styles: Record<string, {
    wrapper: string;
    iconBg: string;
    iconColor: string;
  }> = {
    default: {
      wrapper: 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700',
      iconBg: 'bg-gray-50 dark:bg-gray-700/50',
      iconColor: 'text-gray-600 dark:text-gray-300'
    },
    success: {
      wrapper: 'bg-white dark:bg-gray-800 border-emerald-100 dark:border-emerald-500/20',
      iconBg: 'bg-emerald-50 dark:bg-emerald-500/10',
      iconColor: 'text-emerald-600 dark:text-emerald-400'
    },
    danger: {
      wrapper: 'bg-white dark:bg-gray-800 border-red-100 dark:border-red-500/20',
      iconBg: 'bg-red-50 dark:bg-red-500/10',
      iconColor: 'text-red-600 dark:text-red-400'
    },
    warning: {
      wrapper: 'bg-white dark:bg-gray-800 border-amber-100 dark:border-amber-500/20',
      iconBg: 'bg-amber-50 dark:bg-amber-500/10',
      iconColor: 'text-amber-600 dark:text-amber-400'
    },
    info: {
      wrapper: 'bg-white dark:bg-gray-800 border-blue-100 dark:border-blue-500/20',
      iconBg: 'bg-blue-50 dark:bg-blue-500/10',
      iconColor: 'text-blue-600 dark:text-blue-400'
    }
  };
  const s = styles[accent];
  return <div className={`relative overflow-hidden rounded-3xl border ${s.wrapper} p-6 shadow-sm hover:shadow-md transition-all duration-300 group`} data-id="element-3746" data-mp="2m7j873rf51bx">
      
      {/* Subtle background effect */}
      <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-500 ${s.iconBg}`} data-id="element-3747" data-mp="1tivdyiooz1m8" />
      

      <div className="relative z-10 flex flex-col gap-4" data-id="element-3748" data-mp="277fa3mul97ig">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.iconBg} ${s.iconColor}`} data-id="element-3749" data-mp="2c3w1cpx341zr">
          
          {icon}
        </div>
        <div data-id="element-3750" data-mp="2h1xgizre3h5y">
          <h4 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight mb-1" data-id="element-3751" data-mp="2lu8ie85oxd15">
            {value}
          </h4>
          <p className="text-sm font-semibold text-gray-500 dark:text-gray-400" data-id="element-3752" data-mp="2pvaissbcfv4p">
            {label}
          </p>
        </div>
      </div>
    </div>;
}
function PerformanceGoalCard({
  title,
  current,
  target,
  format,
  icon,
  color
}: {
  title: string;
  current: number;
  target: number;
  format: 'number' | 'currency';
  icon: React.ReactNode;
  color: 'blue' | 'emerald' | 'purple';
}) {
  const percentage = target > 0 ? Math.min(Math.round(current / target * 100), 100) : 0;
  const isCompleted = current >= target;
  const colorStyles = {
    blue: {
      bg: 'bg-blue-50 dark:bg-blue-500/10',
      bar: 'bg-blue-500',
      text: 'text-blue-600 dark:text-blue-400'
    },
    emerald: {
      bg: 'bg-emerald-50 dark:bg-emerald-500/10',
      bar: 'bg-emerald-500',
      text: 'text-emerald-600 dark:text-emerald-400'
    },
    purple: {
      bg: 'bg-purple-50 dark:bg-purple-500/10',
      bar: 'bg-purple-500',
      text: 'text-purple-600 dark:text-purple-400'
    }
  };
  const s = colorStyles[color];
  return <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-all" data-id="element-3753" data-mp="agyz7q1blrbr">
      <div className="flex items-center justify-between mb-4" data-id="element-3754" data-mp="1hlpqxd752r1">
        <div className="flex items-center gap-3" data-id="element-3755" data-mp="1k7s8il8cs2e">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.bg}`} data-id="element-3756" data-mp="3snwj07bysz7t">
            
            {icon}
          </div>
          <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-3757" data-mp="2h9iyncgf71wj">{title}</h3>
        </div>
        {isCompleted && <Badge variant="success" className="animate-pulse" data-id="element-3758" data-mp="2iqy6snwinlp4">
            ¡Logrado!
          </Badge>}
      </div>

      <div className="mb-4" data-id="element-3759" data-mp="2noq6i43ekc3v">
        <div className="flex items-end gap-2" data-id="element-3760" data-mp="2nozyfx80ctc7">
          <span className="text-3xl font-extrabold text-gray-900 dark:text-white" data-id="element-3761" data-mp="29qjg190s9ee8">
            {format === 'currency' ? formatCurrency(current) : current}
          </span>
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-3762" data-mp="2eqxsr4dbxcil">
            / {format === 'currency' ? formatCurrency(target) : target}
          </span>
        </div>
      </div>

      <div className="relative w-full h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-3763" data-mp="24srsssb32ggd">
        <div className={`absolute top-0 left-0 h-full rounded-full transition-all duration-1000 ${s.bar}`} style={{
        width: `${percentage}%`
      }} data-id="element-3764" data-mp="1v13s00smzn28" />
        
      </div>
      <div className="flex justify-between items-center mt-2" data-id="element-3765" data-mp="1m0fmjuhb6v0y">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-3766" data-mp="1lznrqlvjd1bq">
          Progreso
        </span>
        <span className={`text-xs font-bold ${s.text}`} data-id="element-3767" data-mp="12gqcb4f9iqx4">{percentage}%</span>
      </div>
    </div>;
}